import React, { useEffect, useState } from 'react';
import { Trans } from '@ecg-marktplaats/js-react-i18n';

import { Icon, UtilsBlock, TextLarge, TextRegular } from '@hz-design-system/web-ui';
import { currency } from '@ecg-internal/aurora-js-formatters';

import Classes from './OneStepInfoBidding.module.scss';

import { TListingClient } from 'types/TListing';

const getBidsToRender = (bids, userId) => {
  if (!bids.length) return undefined;

  const sortedBids = [...bids].toSorted((a, b) => b.value - a.value) || [];
  const maxBid = sortedBids[0];
  if (maxBid.user.id === userId) {
    return [maxBid];
  }
  const maxSellerBid = sortedBids.find(({ user }) => user.id === userId);
  return maxSellerBid ? [maxBid, maxSellerBid] : [maxBid];
};

type TOneStepInfoBiddingProps = {
  bids: TListingClient['bidsInfo']['bids'];
  loggedUserId: number;
  onClickHandler: () => void;
};

const OneStepInfoBidding = ({ bids, loggedUserId, onClickHandler }: TOneStepInfoBiddingProps) => {
  const bidsToRender = getBidsToRender(bids, loggedUserId);

  // we need this to prevent hydration issues
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);
  if (!mounted) return null;

  return (
    <UtilsBlock p="s" className={Classes.root}>
      <TextLarge color="text-secondary" as="strong">
        <Trans i18nKey="bidding.modal.bids.title" amount={bids ? bids.length : 0} />
      </TextLarge>
      {bidsToRender?.length ? (
        <UtilsBlock pt="xxxs">
          {bidsToRender.map((bidToRender, index) => (
            <UtilsBlock
              pt="xxs"
              pb="xxxs"
              className={Classes.bidInfo}
              tabIndex={0}
              role="button"
              onClick={onClickHandler}
              key={bidToRender.id}
            >
              <TextRegular>
                {bidToRender?.user?.id === loggedUserId ? (
                  <Trans i18nKey="bidding.modal.bids.ownerBid" />
                ) : (
                  <Trans i18nKey="bidding.modal.bids.highestBid" />
                )}
              </TextRegular>
              <div>
                <TextRegular as="strong" color={index ? 'text-error' : 'text-primary'}>
                  {currency(bidToRender.value, true)}
                </TextRegular>
                <Icon name="arrow-right-regular" />
              </div>
            </UtilsBlock>
          ))}
        </UtilsBlock>
      ) : (
        <UtilsBlock pt="s" className={Classes.noBidInfo}>
          <TextRegular>
            <Trans i18nKey="bidding.noBidding" />
          </TextRegular>
        </UtilsBlock>
      )}
    </UtilsBlock>
  );
};

export default OneStepInfoBidding;
